import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  spaceDetailQueryOptions,
  invalidateSpaceScopedQueries,
} from "@/modules/spaces/spaces-queries";
import { useSpaceStore } from "@/modules/spaces/space-store";
import { SpaceAccessDialog } from "@/modules/spaces/SpaceAccessDialog";
import { useAgentDraftStore } from "@/modules/agents/agent-draft-store";
import { sessionsInSpaceQueryOptions } from "../queries/session-queries";
import { defaultAgentQueryOptions } from "../queries/agents-queries";
import {
  connectorsQueryOptions,
  connectorLabel,
} from "../queries/connectors-queries";
import { formatHistoryTime } from "../lib/format-history-time";
import { ConnectorIcon } from "@/shared/ui/ConnectorIcon";
import { SessionTypeIcon } from "./SessionTypeIcon";
import { InputBar } from "./InputBar";
import { WebdavBrowser } from "./WebdavBrowser";
import { DeleteSessionDialog } from "./DeleteSessionDialog";
import { useSessions } from "../hooks/use-sessions";
import { prehashFolder } from "../hooks/use-doc-sync";
import { featuresQueryOptions } from "../queries/features-queries";
import {
  AgentsIcon,
  HistoryIcon,
  NewChatIcon,
  RoutinesIcon,
} from "@/shared/icons";
import { RoutinesView, useRoutineUi } from "@/modules/routines";
import type { SessionInfo } from "../types";

interface Props {
  spaceId: string;
}

/**
 * Дашборд пространства — что юзер видит при открытии «/spaces/:id».
 *
 * Сценарий: юзер выбирает пространство в сайдбаре, попадает сюда. Может:
 * — посмотреть участников, описание;
 * — быстро запустить новую задачу (хиро-инпут с черновиком, который
 *   передаётся в обычный ChatPage через `useAgentDraftStore.pendingText`);
 * — открыть одну из недавних задач этого пространства;
 * — переключиться на агента из чипсов («Ресерчер», «Юрист», …) — это
 *   просто пресетит `pendingRef`, и следующее сообщение полетит к нему.
 *
 * Артефакты / Знания / «Инструменты пространства» — пока заглушки. Когда
 * у нас появятся артефакты как сущность (см. TECH_DEBT), карточка станет
 * реальной.
 *
 * Sidebar и chat-layout-обёртка приходят из родительского `_app`-лейаута,
 * поэтому здесь рендерится только правая (контентная) часть.
 */
export function SpaceDashboard({ spaceId }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: detail, isLoading } = useQuery(spaceDetailQueryOptions(spaceId));
  const { data: sessions = [] } = useQuery(sessionsInSpaceQueryOptions(spaceId));
  const { data: defaultAgent } = useQuery(defaultAgentQueryOptions());
  // `scope=all` returns connectors across every space the user can see, each
  // tagged with its `space.id`. Filter to *this* space's pinned ones — the
  // ones that auto-attach to every agent in the space.
  const { data: connectorsAll = [] } = useQuery(connectorsQueryOptions("all"));
  const spaceConnectors = useMemo(
    () => connectorsAll.filter((c) => c.space?.id === spaceId),
    [connectorsAll, spaceId],
  );
  const setPendingRef = useAgentDraftStore((s) => s.setPendingRef);
  const setPendingText = useAgentDraftStore((s) => s.setPendingText);
  const setPendingCommand = useAgentDraftStore((s) => s.setPendingCommand);
  const setStarting = useAgentDraftStore((s) => s.setStarting);
  const pendingFolderHandle = useAgentDraftStore((s) => s.pendingFolderHandle);
  const setPendingFolderHandle = useAgentDraftStore((s) => s.setPendingFolderHandle);
  const pendingWebdavItems = useAgentDraftStore((s) => s.pendingWebdavItems);
  const setPendingWebdavItems = useAgentDraftStore((s) => s.setPendingWebdavItems);
  const { data: features } = useQuery(featuresQueryOptions());
  const networkFolderEnabled = !!features?.networkFolder;
  const [webdavOpen, setWebdavOpen] = useState(false);
  const setCurrentSpaceId = useSpaceStore((s) => s.setCurrentSpaceId);
  const { deleteSession, selectSession, startNew } = useSessions();
  const showSessionStarter = detail?.session_visibility === "shared";

  // Который из табов активен — «Новая задача» (default), «История» или «Автоматизации».
  // Локально, не в URL: смена спейса не сбрасывает выбор юзера, но
  // навигация прочь (sidebar) — да, потому что компонент размонтируется.
  const [activeTab, setActiveTab] = useState<"new" | "history" | "routines">("new");
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  // Settings kebab next to the members row — same affordance as the
  // SpacesView card. Click → small popover → «Настройки» opens
  // SpaceAccessDialog (read-only for non-owners; mutations gated there).
  const [menuOpen, setMenuOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  // Reset the routines sub-view when leaving the tab so that returning
  // to it lands on the list, not a stale create/edit detail (the routine
  // ui store is a singleton).
  const resetRoutineUi = useRoutineUi((s) => s.openList);
  useEffect(() => {
    if (activeTab !== "routines") resetRoutineUi();
  }, [activeTab, resetRoutineUi]);

  const awaiting = useMemo(
    () => sessions.filter((s) => s.awaiting_user_input),
    [sessions],
  );

  // Make sure space-scoped queries see this space — useSpaceBootstrap is
  // only mounted by ChatPage. The route's wrapper component flips the
  // store, but a direct deep-link landing here may arrive before that.
  useEffect(() => {
    const cur = useSpaceStore.getState().currentSpaceId;
    if (cur !== spaceId) {
      setCurrentSpaceId(spaceId);
      invalidateSpaceScopedQueries(qc);
    }
  }, [spaceId, setCurrentSpaceId, qc]);

  // Drop a folder picked here if the user leaves the dashboard without
  // sending. `starting` flips on inside `goToChat` right before navigating
  // to the chat route, so the send path keeps the handle (ChatPage attaches
  // it to the new session); any other exit (sidebar nav, opening an existing
  // chat) clears it so it can't get attached to whatever session loads next.
  useEffect(() => {
    return () => {
      if (!useAgentDraftStore.getState().starting) {
        useAgentDraftStore.getState().setPendingFolderHandle(null);
        useAgentDraftStore.getState().setPendingWebdavItems(null);
      }
    };
  }, []);

  const goToChat = useCallback(
    (
      text?: string,
      command?: { label: string },
      agentRef?: string | null,
    ) => {
      if (agentRef !== undefined) setPendingRef(agentRef);
      const hasText = !!(text && text.trim());
      if (hasText) {
        setPendingText(text!.trim());
        // Carry the slash-command label so the handed-over message renders
        // as a chip — without this the dashboard composer dropped it and
        // shared-space commands showed expanded (unlike personal-space).
        setPendingCommand(command ?? null);
        // Raise the flag *before* navigating — gates hero / sidebar pill on
        // the very first render of `/`. Cleared in ChatPage once handleSend
        // resolves. Skipped for the empty-click path so the user lands on
        // the regular welcome screen.
        setStarting(true);
      }
      startNew();
    },
    [setPendingRef, setPendingText, setPendingCommand, setStarting, startNew],
  );

  // Folder pick lives on the dashboard now — we open the FS picker in-place
  // and stash the handle in agent-draft-store. ChatPage attaches it via
  // `docs.finishConnect` once the first message creates a session.
  const noop = useCallback(() => {}, []);
  const folderPickerSupported =
    typeof window !== "undefined" && "showDirectoryPicker" in window;
  const handlePickFolder = useCallback(async () => {
    if (!folderPickerSupported) return;
    try {
      const handle = await (
        window as unknown as {
          showDirectoryPicker: (opts: { mode: string }) => Promise<FileSystemDirectoryHandle>;
        }
      ).showDirectoryPicker({ mode: "readwrite" });
      setPendingFolderHandle(handle);
      prehashFolder(handle).catch(() => {});
    } catch (e) {
      const name = (e as { name?: string })?.name;
      if (name !== "AbortError") console.error("Folder pick error:", e);
    }
  }, [folderPickerSupported, setPendingFolderHandle]);
  const handleDisconnectFolder = useCallback(() => {
    setPendingFolderHandle(null);
    setPendingWebdavItems(null);
  }, [setPendingFolderHandle, setPendingWebdavItems]);

  return (
    <div className="space-dashboard">
      {isLoading || !detail ? (
        <div className="space-dashboard-loading">Загрузка пространства…</div>
      ) : (
        <>
        <div className="space-dashboard-main">
          <header className="space-dashboard-head">
            <div className="space-dashboard-head-text">
              <h1 className="space-dashboard-title">{detail.name}</h1>
              {detail.description && (
                <p className="space-dashboard-desc">{detail.description}</p>
              )}
            </div>
            <div className="space-dashboard-members">
              <div className="space-dashboard-avatars">
                {detail.members.slice(0, 3).map((m) => (
                  <span
                    key={m.user_id}
                    className="space-dashboard-avatar"
                    title={m.name || m.email}
                  >
                    {(m.name || m.email || "?").slice(0, 1).toUpperCase()}
                  </span>
                ))}
              </div>
              <span className="space-dashboard-members-count">
                {detail.members.length} {plural(detail.members.length, "участник", "участника", "участников")}
              </span>
              <div
                className="cmd-card-menu-wrap"
                ref={menuRef}
              >
                <button
                  type="button"
                  className="cmd-card-kebab"
                  onClick={() => setMenuOpen((v) => !v)}
                  aria-label="Действия"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                >
                  <KebabIcon />
                </button>
                {menuOpen && (
                  <div className="cmd-card-menu" role="menu">
                    <button
                      type="button"
                      role="menuitem"
                      className="cmd-card-menu-item"
                      onClick={() => {
                        setMenuOpen(false);
                        setAccessOpen(true);
                      }}
                    >
                      <CogIcon />
                      <span>Настройки</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </header>

          <div className="history-tabs space-dashboard-tabs" role="tablist">
            <DashboardTab
              icon={<NewChatIcon />}
              label="Новая задача"
              active={activeTab === "new"}
              onClick={() => setActiveTab("new")}
            />
            <DashboardTab
              icon={<HistoryIcon />}
              label="История"
              active={activeTab === "history"}
              badge={awaiting.length || undefined}
              onClick={() => setActiveTab("history")}
            />
            <DashboardTab
              icon={<RoutinesIcon />}
              label="Автоматизации"
              active={activeTab === "routines"}
              onClick={() => setActiveTab("routines")}
            />
          </div>

          {activeTab === "new" ? (
            <>
              <div className="space-dashboard-composer">
                <InputBar
                  onSend={(text, command) => goToChat(text, command)}
                  onStop={noop}
                  disabled={false}
                  isStreaming={false}
                  sessionId={null}
                  folderConnected={!!pendingFolderHandle || !!pendingWebdavItems?.length}
                  folderName={pendingFolderHandle?.name ?? (pendingWebdavItems?.length ? "Сетевая папка" : null)}
                  folderPickerSupported={folderPickerSupported}
                  onPickFolder={handlePickFolder}
                  networkFolderEnabled={networkFolderEnabled}
                  onPickNetworkFolder={() => setWebdavOpen(true)}
                  onDisconnectFolder={handleDisconnectFolder}
                  heroMode
                  syncStatus={{
                    active: false,
                    direction: "",
                    current: 0,
                    total: 0,
                    warning: "",
                  }}
                />
              </div>

              <section className="hero-recent space-dashboard-recent">
                <h2 className="hero-recent-title">Недавние</h2>
                {sessions.length === 0 ? (
                  <div className="space-dashboard-empty">
                    Пока пусто. Запустите первую задачу в этом пространстве.
                  </div>
                ) : (
                  <ul className="hero-recent-list">
                    {sessions.slice(0, 3).map((s) => {
                      const agentName = s.agent?.label || s.agent?.name;
                      const isDefaultAgent =
                        !!defaultAgent &&
                        !!s.agent &&
                        s.agent.ref === defaultAgent.ref;
                      const showAgent = !!agentName && !isDefaultAgent;
                      const timeLabel = formatHistoryTime(s.updated_at);
                      const starterLabel = getSessionStarterLabel(s);
                      return (
                        <li key={s.id}>
                          <button
                            type="button"
                            className="hero-task-item"
                            onClick={() => selectSession(s.id)}
                          >
                            <SessionTypeIcon
                              kind={s.routine_id ? "routine" : "chat"}
                              awaiting={s.awaiting_user_input}
                              running={s.is_running}
                              variant="badge"
                              className="hero-task-icon"
                            />
                            <span className="hero-task-content">
                              <span className="hero-task-title-row">
                                <span className="hero-task-title">
                                  {s.title || "Без названия"}
                                </span>
                                {showAgent && (
                                  <span
                                    className="hero-task-tag"
                                    title={`Агент: ${agentName}`}
                                  >
                                    <AgentsIcon className="hero-task-bot-icon" />
                                    <span>{agentName}</span>
                                  </span>
                                )}
                              </span>
                              {(timeLabel || (showSessionStarter && starterLabel)) && (
                                <span className="hero-task-subtitle">
                                  {timeLabel && <span>{timeLabel}</span>}
                                  {showSessionStarter && starterLabel && (
                                    <>
                                      {timeLabel && <span> · </span>}
                                      <span>{starterLabel}</span>
                                    </>
                                  )}
                                </span>
                              )}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {sessions.length > 3 && (
                  <button
                    type="button"
                    className="hero-recent-more"
                    onClick={() => setActiveTab("history")}
                  >
                    <span>Показать больше</span>
                    <ArrowUpRightIcon />
                  </button>
                )}
              </section>
            </>
          ) : activeTab === "history" ? (
            <SpaceHistorySection
              sessions={sessions}
              awaiting={awaiting}
              showSessionStarter={showSessionStarter}
              onOpenSession={selectSession}
              onDeleteSession={setDeleteTarget}
            />
          ) : (
            <RoutinesView scope={{ kind: "space", id: spaceId }} />
          )}
        </div>

        <aside className="space-dashboard-side">
          <SideCard
            title="Коннекторы"
            actionLabel="Добавить"
            onAction={() => navigate({ to: "/connectors" })}
          >
            {spaceConnectors.length === 0 ? (
              <div className="space-dashboard-side-empty">
                В этом пространстве пока нет общих коннекторов.
                Добавьте MCP — и его инструменты появятся у всех агентов.
              </div>
            ) : (
              <ul className="space-dashboard-connectors">
                {spaceConnectors.map((c) => {
                  const url = (c.config?.url as string | undefined) ?? "";
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        className="space-dashboard-connector-item"
                        onClick={() => navigate({ to: "/connectors" })}
                        title={c.description || connectorLabel(c)}
                      >
                        <ConnectorIcon url={url || null} label={connectorLabel(c)} size={20} />
                        <span className="space-dashboard-connector-name">
                          {connectorLabel(c)}
                        </span>
                        {!c.enabled && (
                          <span className="space-dashboard-connector-badge">Выключен</span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </SideCard>
          <SideCard title="Артефакты" stub>
            <div className="space-dashboard-side-empty">
              Появятся, когда агенты начнут производить документы.
            </div>
          </SideCard>
          <SideCard title="Знания" stub>
            <div className="space-dashboard-side-empty">
              База знаний пространства — пока в разработке.
            </div>
          </SideCard>
        </aside>
        <DeleteSessionDialog
          open={deleteTarget !== null}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => {
            if (deleteTarget) deleteSession(deleteTarget);
            setDeleteTarget(null);
          }}
        />
        {accessOpen && (
          <SpaceAccessDialog
            spaceId={spaceId}
            onClose={() => setAccessOpen(false)}
          />
        )}
        {webdavOpen && (
          <WebdavBrowser
            sessionId={null}
            onStash={setPendingWebdavItems}
            onImported={() => { /* hero — import happens after session create */ }}
            onClose={() => setWebdavOpen(false)}
          />
        )}
        </>
      )}
    </div>
  );
}

function KebabIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <circle cx="8" cy="3.5" r="1.4" />
      <circle cx="8" cy="8" r="1.4" />
      <circle cx="8" cy="12.5" r="1.4" />
    </svg>
  );
}

function CogIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function SpaceHistorySection({
  sessions,
  awaiting,
  showSessionStarter,
  onOpenSession,
  onDeleteSession,
}: {
  sessions: SessionInfo[];
  awaiting: SessionInfo[];
  showSessionStarter: boolean;
  onOpenSession: (id: string) => void;
  onDeleteSession: (id: string) => void;
}) {
  const { data: defaultAgent } = useQuery(defaultAgentQueryOptions());
  const renderItem = (s: SessionInfo) => {
    const agentName = s.agent?.label || s.agent?.name;
    const isDefaultAgent =
      !!defaultAgent && !!s.agent && s.agent.ref === defaultAgent.ref;
    const showAgent = !!agentName && !isDefaultAgent;
    const timeLabel = formatHistoryTime(s.updated_at);
    const starterLabel = getSessionStarterLabel(s);
    return (
      <div
        key={s.id}
        className={`history-item${s.awaiting_user_input ? " history-item--awaiting" : ""}`}
        onClick={() => onOpenSession(s.id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter") onOpenSession(s.id);
        }}
      >
        <SessionTypeIcon
          kind={s.routine_id ? "routine" : "chat"}
          awaiting={s.awaiting_user_input}
          running={s.is_running}
          variant="badge"
          className="history-item-icon"
        />
        <div className="history-item-content">
          <div className="history-item-title-row">
            <span className="history-item-title">{s.title || "Новый чат"}</span>
            {showAgent && (
              <span className="history-item-agent" title={`Агент: ${agentName}`}>
                <AgentsIcon className="history-item-bot-icon" />
                <span>{agentName}</span>
              </span>
            )}
          </div>
          {(timeLabel || (showSessionStarter && starterLabel)) && (
            <div className="history-item-meta">
              {timeLabel && <span>{timeLabel}</span>}
              {showSessionStarter && starterLabel && (
                <span>
                  {timeLabel ? "· " : ""}
                  {starterLabel}
                </span>
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          className="history-item-delete"
          onClick={(e) => {
            e.stopPropagation();
            onDeleteSession(s.id);
          }}
          title="Удалить"
          aria-label="Удалить"
        >
          ×
        </button>
      </div>
    );
  };

  return (
    <>
      {awaiting.length > 0 && (
        <section className="history-section">
          <h2 className="cmd-page-section-title">Требуют действия</h2>
          <div className="history-list">{awaiting.map(renderItem)}</div>
        </section>
      )}
      <section className="history-section">
        <h2 className="cmd-page-section-title">Недавние</h2>
        {sessions.length === 0 ? (
          <div className="history-empty">Завершённых задач пока нет.</div>
        ) : (
          <div className="history-list">{sessions.map(renderItem)}</div>
        )}
      </section>
    </>
  );
}

function getSessionStarterLabel(session: SessionInfo): string | null {
  const name = session.owner?.name?.trim();
  if (name) return name;
  const email = session.owner?.email?.trim();
  if (email) return email;
  return null;
}

function DashboardTab({
  icon,
  label,
  active,
  badge,
  disabled,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  badge?: number;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={!!active}
      className={`history-tab${active ? " active" : ""}`}
      disabled={disabled}
      onClick={onClick}
      title={disabled ? "Скоро" : undefined}
    >
      <span className="space-dashboard-tab-icon">{icon}</span>
      <span>{label}</span>
      {badge ? <span className="space-dashboard-tab-badge">{badge}</span> : null}
    </button>
  );
}

function SideCard({
  title,
  children,
  stub,
  actionLabel,
  onAction,
}: {
  title: string;
  children?: React.ReactNode;
  stub?: boolean;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <section className="space-dashboard-side-card">
      <header className="space-dashboard-side-head">
        <span>{title}</span>
        {stub && <span className="space-dashboard-side-stub">скоро</span>}
        {actionLabel && onAction && (
          <button
            type="button"
            className="space-dashboard-side-action"
            onClick={onAction}
          >
            {actionLabel}
          </button>
        )}
      </header>
      {children}
    </section>
  );
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

function ArrowUpRightIcon() {
  return (
    <svg viewBox="0 0 9.584 9.584" fill="none" aria-hidden="true">
      <path d="M8.95898 0C9.3039 0.000175808 9.58381 0.28008 9.58398 0.625V8.95898C9.58381 9.3039 9.3039 9.58381 8.95898 9.58398C8.61392 9.58398 8.33416 9.30401 8.33398 8.95898V2.13379L1.06738 9.40039C0.823305 9.64447 0.427671 9.64447 0.183594 9.40039C-0.0604839 9.15631 -0.0604839 8.76068 0.183594 8.5166L7.4502 1.25H0.625C0.279972 1.24982 0 0.970069 0 0.625C0.000175727 0.28008 0.28008 0.000175727 0.625 0H8.95898Z" fill="currentColor" />
    </svg>
  );
}
